import { useEffect, useRef, useCallback } from "react";

interface TurnstileWidgetProps {
  onToken: (token: string) => void;
  siteKey?: string;
}

export default function TurnstileWidget({
  onToken,
  siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || "1x00000000000000000000AA",
}: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const stableOnToken = useCallback(onToken, [onToken]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const renderWidget = () => {
      const turnstile = (window as any).turnstile;
      if (!turnstile || !container) return;

      // Clean up any existing widget before rendering
      if (widgetIdRef.current !== null) {
        try {
          turnstile.remove(widgetIdRef.current);
        } catch {
          // Ignore cleanup errors
        }
        widgetIdRef.current = null;
      }

      widgetIdRef.current = turnstile.render(container, {
        sitekey: siteKey,
        callback: stableOnToken,
        theme: "dark",
        size: "compact",
      });
    };

    // Check if Turnstile is already loaded
    if ((window as any).turnstile) {
      renderWidget();
    } else {
      // Poll for Turnstile to load
      const interval = setInterval(() => {
        if ((window as any).turnstile) {
          clearInterval(interval);
          renderWidget();
        }
      }, 100);

      return () => clearInterval(interval);
    }

    // Cleanup on unmount
    return () => {
      if (widgetIdRef.current !== null) {
        try {
          (window as any).turnstile?.remove(widgetIdRef.current);
        } catch {
          // Ignore cleanup errors
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, stableOnToken]);

  return <div ref={containerRef} />;
}
